import {
  StyledDialog,
  StyledDialogTitle,
} from "../components/StyledComponents/Dialog/Dialog.js";

import React, { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Autocomplete,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Portal,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
  StyledLabel,
  StyledToggleButton,
} from "../components/StyledComponents/Buttons/AuradineButtons.js";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import {
  BodyMedium,
  H8TitleMedium,
  H6HeadlineSmall,
  H9TitleSmall,
  BodyLarge,
} from "../components/StyledComponents/Typography/Typography.tsx";
import { useForm, Controller } from "react-hook-form";
import PlaceHolderButton from "../components/PlaceHolderButton/PlaceHolderButton.js";
import { usePostOrg } from "../api/queries/usePostOrg.ts";
import { useAuth0 } from "@auth0/auth0-react";
import {
  StyledCheckbox,
  StyledFormControl,
  StyledInputLabel,
  StyledSelect,
  StyledTextField,
} from "../components/StyledComponents/Inputs/Inputs.js";

import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import * as api from "../api/api.js";
import { useGetSiteLists } from "../api/queries/useGetSiteLists.ts";
import { useAttachDetachPermissions } from "./queries/useAttachDetachPermissions.ts";
import { useAssignRole } from "./queries/useAssignRole.ts";
import { useDeleteUser } from "./queries/useDeleteUser.ts";
import ActionDialog from "../components/ActionDialog/ActionDialog.js";
import { useTheme } from "../ThemeContext/ThemeContext.js";
import { StyledMenuItem } from "../components/StyledComponents/Menu/Menu.tsx";
  
const ResourceSelectionFormHeader = ({
  setOpen,
  formReset,
  setActiveStep,
}) => {
  const { colors } = useTheme();
  console.log("colors", colors);
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <H6HeadlineSmall>
        What would you like to add?
      </H6HeadlineSmall>

      <StyledIconButton
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const UserForm = ({
  open,
  handleClose,
  onMoveBack,
  setActiveStep,
  selectedRow,
  setSelectedRow,
  role,
  name,
  refetch: orgDataRefetch,
  email,
}) => {
  const { colors } = useTheme();
  console.log("role", role);
  const userRoles = [
    "superadmin",
    "pooladmin",
    "admin",
    "readonly",
  ];

  const roleHierarchy = {
    superadmin: 4,
    pooladmin: 3,
    admin: 2,
    readonly: 1,
  };

  const [deletionDialogOpen, setDeletionDialogOpen] =
    useState(false);

  const isRoleDisabled = (targetRole) => {
    const myLevel = roleHierarchy[role];
    const targetLevel = roleHierarchy[targetRole];
    console.log(
      `→ disabling? "${targetRole}" (lvl ${targetLevel}) >= "${role}" (lvl ${myLevel}) →`,
      targetLevel >= myLevel
    );
    return targetLevel >= myLevel;
  };

  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [siteEditModeOn, setSiteEditModeOn] =
    useState(false);
  const [userRoleEditMode, setUserRoleEditMode] =
    useState(false);
  const [severityLevel, setSeverityLevel] = useState("");
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const [authToken, setAuthToken] = useState("");
  const { isAuthenticated, getAccessTokenSilently } =
    useAuth0(); // Use the useAuth0 hook to get authentication status and token fetching function
  const selectedSites =
    selectedRow?.permissions &&
    selectedRow?.permissions.sites
      ? selectedRow.permissions?.sites
      : [];
  console.log(selectedSites, "selectedSites");

  const labelFormData = [
    {
      label: "User name",
      value: selectedRow?.name,
    },
    {
      label: "Email",
      value: selectedRow?.email,
    },
    {
      label: "Org",
      value: selectedRow?.org_id,
    },
  ];
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  let membersData = [];

  const {
    data: userData,
    isLoading,
    isFetching,
    isError,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ["user", open],
    queryFn: ({ queryKey }) =>
      api.GetUserUseQuery(authToken),
    enabled: !!authToken && open,
  });
  const orgId =
    isFetched && userData?.result?.org_id
      ? userData?.result?.org_id
      : "";

  const {
    data: members,
    refetch: membersDataRefetch,
    isLoading: isMembersDataLoading,
    isFetching: isMembersDataFetching,
    isFetched: isMembersDataFetched,
  } = useQuery({
    queryKey: ["membersData", orgId], // Use only serializable values
    queryFn: () => api.GetOrgUseQuery(authToken, orgId), // Pass correct arguments
    enabled: open && !!authToken,
  });

  if (isMembersDataFetched && members) {
    membersData =
      isMembersDataFetched &&
      members?.org?.user &&
      members?.org?.user.length > 0
        ? members?.org?.user.map((user) => user.email)
        : [];
  }

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: true,
  });
  const siteList =
    siteLists &&
    siteLists?.status === "ok" &&
    siteLists?.siteList?.length > 0
      ? siteLists?.siteList.map((site) => site.siteName)
      : [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      userRole: "",
      siteConfig: [],
    },
  });
  useEffect(() => {
    if (siteEditModeOn) {
      setValue("siteConfig", selectedSites);
    } else {
      setValue("siteConfig", []);
    }
  }, [siteEditModeOn]);

  useEffect(() => {
    if (userRoleEditMode) {
      console.log(
        "userRoleEditMode",
        userRoleEditMode,
        role
      );
      reset({
        userRole: selectedRow?.role,
      });
    } else {
      reset({
        userRole: "",
      });
    }
  }, [userRoleEditMode, role, reset]);

  const userRole = watch("userRole") || [];
  const siteOptions = watch("siteConfig") || [];
  console.log(siteEditModeOn, "siteEditModeOn");
  const { mutateAsync: postPermissions } =
    useAttachDetachPermissions();

  const onSiteSubmit = React.useCallback(
    async (data) => {
      if (siteEditModeOn) {
        try {
          console.log("Saving siteConfig data:", data);
          const changes = siteOptions.filter(
            (site) => !selectedSites.includes(site)
          );
          if (changes.length > 0) {
            const results = await Promise.allSettled(
              changes.map((site) =>
                postPermissions({
                  resourceType: "sites",
                  resourceName: site,
                  permissionOp: "attach",
                  targetUsers: [selectedRow?.email],
                  token: authToken, // Include the token property
                })
              )
            );
            const allFullfilled = results.every(
              (result) => result.status === "fulfilled"
            );
            console.log("Results:", allFullfilled);
            if (allFullfilled) {
              setToastMessage(
                "Site permissions updated successfully"
              );
              await orgDataRefetch();
              setSelectedRow((prev) => ({
                ...prev,
                permissions: {
                  ...prev.permissions,
                  sites: siteOptions,
                },
              }));
              setToastOpen(true);
              setSiteEditModeOn(false);
            } else {
              const errors = results
                .filter(
                  (result) => result.status === "rejected"
                )
                .map((result) => result.reason);
              console.error("Errors:", errors);
              setToastMessage(
                "Failed to save changes for user"
              );
              setToastOpen(true);
            }
          } else {
            setSiteEditModeOn(false);
          }
        } catch (err) {
          console.error(
            "Failed to update site details for user :",
            err
          );
          setToastMessage(
            "Failed to save changes for user"
          );
          setToastOpen(true);
        }
      }
    },
    [
      siteEditModeOn,
      siteOptions,
      selectedSites,
      postPermissions,
      selectedRow?.email,
      authToken,
      setToastMessage,
      setToastOpen,
    ]
  );

  const handleSiteConfigDeletion = (optionToDelete) => {
    if (!siteEditModeOn) {
      const newValue = siteOptions.filter(
        (option) => option !== optionToDelete
      );
      setValue("siteConfig", newValue);
    } else {
      const newValue = selectedSites.filter(
        (option) => option === optionToDelete
      );
      const updatedSelectedSites = selectedSites.filter(
        (option) => option !== optionToDelete
      );
      postPermissions(
        {
          resourceType: "sites",
          resourceName: optionToDelete,
          permissionOp: "detach",
          targetUsers: [selectedRow?.email],
          token: authToken, // Include the token property
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              setToastMessage(
                "Site permissions updated successfully for the user"
              );
              setToastOpen(true);
              await orgDataRefetch();
              setSelectedRow((prev) => ({
                ...prev,
                permissions: {
                  ...prev.permissions,
                  sites: updatedSelectedSites,
                },
              }));
              reset({
                siteConfig: [...updatedSelectedSites],
              });
            } else {
              setToastMessage(
                "Failed to save site permission changes for user"
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              "Failed to save changes for user"
            );
            setToastOpen(true);
          },
        }
      );
    }
  };
  const { mutate: postUserRole } = useAssignRole();

  const onRoleSubmit = () => {
    const userRole = watch("userRole");
    if (userRoleEditMode) {
      postUserRole(
        {
          email: selectedRow?.email,
          role: userRole,
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              setToastMessage(
                "User role updated successfully"
              );
              setToastOpen(true);
              setSelectedRow((prev) => ({
                ...prev,
                role: userRole,
              }));
              reset({
                userRole: "",
              });
              await orgDataRefetch();
              setUserRoleEditMode(false);
            } else {
              setToastMessage(
                "Failed to save changes for user"
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              "Failed to save changes for user"
            );
            setToastOpen(true);
          },
        }
      );
    }
  };

  const { mutate: deleteUser } = useDeleteUser();

  const handleUserDelete = async () => {
    try {
      deleteUser(
        {
          email: selectedRow?.email,
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              setToastMessage("User deleted successfully");
              setToastOpen(true);
              await orgDataRefetch();
              handleClose();
            } else {
              setToastMessage("Failed to delete user");
              setToastOpen(true);
            }
            setDeletionDialogOpen(false);
          },
          onError: (error) => {
            setToastMessage("Failed to delete user");
            setToastOpen(true);
            setDeletionDialogOpen(false);
          },
        }
      );
    } catch (error) {
      console.error("Error deleting user:", error);
      setToastMessage("Failed to delete user");
      setToastOpen(true);
    }
  };

  return (
    <>
      <StyledDialogTitle>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
          >
            <H6HeadlineSmall style={{ textAlign: "left" }}>
              User Profile
            </H6HeadlineSmall>
            <BodyMedium
              style={{ textAlign: "left" }}
            ></BodyMedium>
          </Box>
          <StyledIconButton
            colors={colors}
            onClick={() => {
              handleClose();
              reset();
            }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {showAlert && (
          <Portal>
            <Box
              padding={"0 0.5rem 0.5rem 0.5rem"}
              display={"flex"}
              flexGrow={0}
              sx={{
                position: "fixed",
                top: "30%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000, // Ensure it appears above the dialog
                width: "auto",
              }}
            >
              <Alert
                severity={"error"}
                onClose={() => setShowAlert(false)}
                sx={{ padding: "0.5rem 0.75rem", color: colors?.primary?.main }}
              >
                {alertText}
              </Alert>
            </Box>
          </Portal>
        )}
        <Box
          display="flex"
          flexDirection={"column"}
          gap={8}
          // paddingTop={"2.5rem"}
          paddingBottom={"2.5rem"}
        >
          <H8TitleMedium>User Information</H8TitleMedium>
          <Box
            display={"grid"}
            gridTemplateColumns={"1fr 2fr"}
            padding={"0.5rem"}
            borderRadius={"0.5rem"}
            sx={{ backgroundColor: colors.background.accent }}
            gap={6}
          >
            {labelFormData.map((item, index) => (
              <>
                <H9TitleSmall>{item?.label}</H9TitleSmall>
                <BodyLarge>{item?.value}</BodyLarge>
              </>
            ))}
          </Box>
        </Box>
        <form>
          <Box
            display="flex"
            flexDirection="column"
            gap={6}
            flexGrow={1}
          >
            {siteEditModeOn && (
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="sitesContainer"
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignContent={"center"}
                >
                  <H8TitleMedium>
                    Select Sites to assign to the user
                  </H8TitleMedium>
                  <Box>
                    <StyledIconButton
                      colors={colors}
                      onClick={() =>
                        setSiteEditModeOn(false)
                      }
                      type="button"
                    >
                      <span className="material-symbols-outlined">
                        close
                      </span>
                    </StyledIconButton>
                    <StyledIconButton
                      colors={colors}
                      onClick={onSiteSubmit}
                      type="button"
                      disabled={role !== "superadmin"}
                    >
                      <span className="material-symbols-outlined">
                        save
                      </span>
                    </StyledIconButton>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl
                    fullWidth
                    margin="none"
                    colors={colors}
                  >
                    {/*
                     <InputLabel htmlFor="">Status</InputLabel> */}
                    <Controller
                      name="siteConfig"
                      control={control}
                      defaultValue={null}
                      render={({
                        field: { onChange, value, ref },
                      }) => (
                        <Autocomplete
                          fullWidth
                          multiple
                          disableCloseOnSelect
                          onChange={(_, data) =>
                            onChange(data)
                          }
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted , 
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          value={value || []}
                          clearIcon={null}
                          getOptionDisabled={(option) =>
                            siteEditModeOn &&
                            selectedSites.includes(option)
                          }
                          options={siteList} // Replace with your options
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Site Name"
                              variant="outlined"
                              inputRef={ref}
                              sx={{ minHeight: "2.5rem" }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {isSiteGroupDataFetching ||
                                    isSiteGroupDataLoading ? (
                                      <CircularProgress
                                        size={24}
                                        sx={{
                                          color: colors.primary.main,
                                        }}
                                      />
                                    ) : null}
                                    {
                                      params.InputProps
                                        .endAdornment
                                    }
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </StyledFormControl>
                </Box>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={4}
                  maxWidth={"62.5rem"}
                  flexWrap={"wrap"}
                >
                  {siteOptions?.map((site, index) => (
                    <PlaceHolderButton
                      key={index}
                      onClose={() =>
                        handleSiteConfigDeletion(site)
                      }
                      text={site}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {!siteEditModeOn &&
              selectedRow.role !== "superadmin" && (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={2}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignContent={"center"}
                  >
                    <H8TitleMedium>
                      Sites assigned to the user.
                    </H8TitleMedium>
                    <StyledIconButton
                      colors={colors}
                      type="button"
                      disabled={role !== "superadmin"}
                      onClick={() =>
                        setSiteEditModeOn(true)
                      }
                    >
                      <span className="material-symbols-outlined" >
                        edit
                      </span>
                    </StyledIconButton>
                  </Box>
                  {selectedSites.length === 0 &&
                  selectedRow.role !== "superadmin" ? (
                    <BodyMedium>
                      No sites assigned to the user
                    </BodyMedium>
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={4}
                      maxWidth="62.5rem"
                      flexWrap="wrap"
                    >
                      {selectedSites.map((site) => (
                        <StyledLabel key={site} colors={colors}>
                          <BodyMedium>{site}</BodyMedium>
                        </StyledLabel>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            {userRoleEditMode ? (
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="userRoleContainer"
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignContent={"center"}
                >
                  <H8TitleMedium>
                    Select role to assign to the user
                  </H8TitleMedium>
                  <Box>
                    <StyledIconButton
                      colors={colors}
                      onClick={() =>
                        setUserRoleEditMode(false)
                      }
                      type="button"
                    >
                      <span className="material-symbols-outlined" >
                        close
                      </span>
                    </StyledIconButton>
                    <StyledIconButton
                      colors={colors}
                      onClick={onRoleSubmit}
                      type="button"
                    >
                      <span className="material-symbols-outlined" >
                        save
                      </span>
                    </StyledIconButton>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl
                    fullWidth
                    margin="none"
                    colors={colors}
                  >
                    <StyledInputLabel htmlFor="" colors={colors}
                    >
                      Role
                    </StyledInputLabel>
                    <Controller
                      name="userRole"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <StyledSelect
                          {...field}
                          colors={colors}
                          label="Role"
                          value={field.value || ""}
                          style={{ flex: 1 }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                              },
                            },
                          }}
                        >
                          {userRoles?.map((role, index) => (
                            <StyledMenuItem colors={colors}
                              key={index}
                              value={role}
                              disabled={isRoleDisabled(
                                role
                              )}
                            >
                              {role}
                            </StyledMenuItem>
                          ))}
                        </StyledSelect>
                      )}
                    />
                  </StyledFormControl>
                </Box>
              </Box>
            ) : (
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={2}
              >
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                  alignContent={"center"}
                >
                  <H8TitleMedium>
                    Role assigned to user
                  </H8TitleMedium>
                  {selectedRow.name !== name &&
                    selectedRow.role !== "superadmin" && (
                      <StyledIconButton
                        colors={colors}
                        type="button"
                        disabled={role !== "superadmin"}
                        onClick={() =>
                          setUserRoleEditMode(true)
                        }
                      >
                        <span className="material-symbols-outlined" >
                          edit
                        </span>
                      </StyledIconButton>
                    )}
                </Box>
                {userRole === "" ? (
                  <BodyMedium>
                    No role assigned to the user
                  </BodyMedium>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={4}
                    maxWidth="62.5rem"
                    flexWrap="wrap"
                  >
                    <StyledLabel colors={colors}>
                      <BodyMedium>
                        {selectedRow.role === "user" ? "readonly" : selectedRow.role}
                      </BodyMedium>
                    </StyledLabel>
                  </Box>
                )}
              </Box>
            )}
            {role === "superadmin" &&
          email !== selectedRow.email &&
          selectedRow.role !== "superadmin" && (
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            alignItems={"flex-start"}
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignContent={"center"}
              >
                {selectedRow?.name?.trim() ? <H8TitleMedium>Do you want to delete user {selectedRow.name}?</H8TitleMedium> : <H8TitleMedium>Do you want to delete this user?</H8TitleMedium>}
              </Box>
              <SecondaryButton colors={colors}
              disabled={role !== "superadmin"}
              onClick={() => setDeletionDialogOpen(true)}
            >
              Delete User
            </SecondaryButton>
          </Box>
          )}
          </Box>
        </form>
      </DialogContent>
      <ActionDialog
        open={deletionDialogOpen}
        onClose={() => setDeletionDialogOpen(false)}
        headerMessage={(selectedRow?.name?.trim()) ?  `Delete user ${selectedRow.name}?` : `Delete this user?`}
        subMessage={"This action cannot be undone."}
        buttonText={"Delete"}
        deleteAction={handleUserDelete}
      />
    </>
  );
};

const SitePermissionsDialog = ({
  open,
  handleClose,
  selectedRow,
  setSelectedRow,
  role,
  name,
  refetch,
  email,
}) => {
  const { colors } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [severityLevel, setSeverityLevel] = useState("");
  const onMoveForward = (data) => {
    setActiveStep((prev) => prev + 1);
  };
  const onMoveBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <StyledDialog
      colors={colors}
      maxWidth="md"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <UserForm
        open={open}
        handleClose={handleClose}
        onMoveBack={onMoveBack}
        setActiveStep={setActiveStep}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        role={role}
        name={name}
        refetch={refetch}
        email={email}
      />
    </StyledDialog>
  );
};

export const UserProfileDialog = ({
  open,
  handleClose,
  selectedRow,
  setSelectedRow,
  role,
  name,
  refetch,
  email,
}) => {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <SitePermissionsDialog
        open={open}
        handleClose={handleClose}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        role={role}
        name={name}
        refetch={refetch}
        email={email}
      />
    </QueryClientProvider>
  );
};

export default UserProfileDialog;
